import CountUp from "react-countup";
import { Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { memo, useMemo } from "react";

import "./SummaryRow.scss";
import { decimalPlaces } from "../../../../../service/utils/functions";
import TopicBlock from "../../../../../component/topic-block/TopicBlock";
import Container from "../../../../../component/container/Container";
import { GenericGraphDataEntry } from "../../../../../service/utils/interfaces";
import SortableTable from "../../../../../component/sortable-table/SortableTable";
import { wasteTypesDetailTableHeader } from "../../../../../service/utils/constants";
import WTPTooltip from "../../../../../component/wtp-tooltip/WTPTooltip";

interface SummaryRowProps {
  isTippingUser: boolean;
  achievementDataArray: any[];
  achievementDataArrayTipping: any[];
  totalWasteCount: number;
  recycledWasteTypes: GenericGraphDataEntry[];
  recycledWasteDetails: any[][];
  colorArray: any[];

  width: number;
  darkMode: boolean;
  animationDuration: number;
}

const SummaryRow = memo((props: SummaryRowProps) => {
  const firstHalfAchievements = useMemo(() => {
    if (!props.achievementDataArray) return [];
    const half = Math.ceil(props.achievementDataArray.length / 2);
    return props.achievementDataArray.filter((_, idx) => idx < half);
  }, [props.achievementDataArray]);

  const secondHalfAchievements = useMemo(() => {
    if (!props.achievementDataArray) return [];
    const half = Math.ceil(props.achievementDataArray.length / 2);
    return props.achievementDataArray.filter((_, idx) => idx >= half);
  }, [props.achievementDataArray]);

  return (
    <div className="row">
      {props.isTippingUser ? (
        <div className="tipping-achievements">
          {props.achievementDataArrayTipping.map((elem, idx) => {
            const isNumberValue = idx === 0 || idx === 4;
            const feeValue = idx === 4;
            return (
              <div
                key={"achievement-tipping-" + idx}
                className="tipping-row-container"
              >
                <TopicBlock darkTheme={props.darkMode}>
                  <div
                    className={"icon-wrapper" + (props.darkMode ? " dark" : "")}
                  >
                    <img src={elem.icon} alt={elem.altText} />
                  </div>
                  <div className="achievement-description">
                    <p>{elem.title}</p>
                  </div>
                  <div className="countup-topic">
                    <p>
                      {isNumberValue
                        ? (feeValue ? "£" : "") +
                          Number(elem.value).toLocaleString("en-GB")
                        : elem.value}
                    </p>
                  </div>
                </TopicBlock>
              </div>
            );
          })}
        </div>
      ) : (
        <>
          <div className="column" style={{ flexGrow: 5, width: "56%" }}>
            <div className="row" style={{ flexGrow: 1 }}>
              {firstHalfAchievements.map((elem, idx) => {
                const decimalsNo = decimalPlaces(elem.value);
                return (
                  <div
                    key={"achievement-r1-" + idx}
                    className="kpi-row-container"
                  >
                    <TopicBlock darkTheme={props.darkMode}>
                      <div
                        className={
                          "icon-wrapper" + (props.darkMode ? " dark" : "")
                        }
                      >
                        <img src={elem.icon} alt={elem.altText} />
                      </div>
                      <CountUp
                        className="countup-topic"
                        decimals={Math.min(decimalsNo, 3)}
                        end={elem.value}
                        delay={0.5}
                        duration={props.animationDuration / 1000}
                        suffix={
                          elem.unit && elem.unit.length > 0
                            ? elem.unit
                            : undefined
                        }
                      />
                      <div className="achievement-description">
                        <p>{elem.title}</p>
                      </div>
                    </TopicBlock>
                  </div>
                );
              })}
            </div>
            <div className="row" style={{ flexGrow: 1 }}>
              {secondHalfAchievements.map((elem, idx) => {
                const decimalsNo = decimalPlaces(elem.value);
                return (
                  <div
                    key={"achievement-r2-" + idx}
                    className="kpi-row-container"
                  >
                    <TopicBlock darkTheme={props.darkMode}>
                      <div
                        className={
                          "icon-wrapper" + (props.darkMode ? " dark" : "")
                        }
                      >
                        <img src={elem.icon} alt={elem.altText} />
                      </div>
                      <CountUp
                        className="countup-topic"
                        decimals={Math.min(decimalsNo, 3)}
                        end={elem.value}
                        delay={0.5}
                        duration={props.animationDuration / 1000}
                        suffix={
                          elem.unit && elem.unit.length > 0
                            ? elem.unit
                            : undefined
                        }
                      />
                      <div className="achievement-description">
                        <p>{elem.title}</p>
                      </div>
                    </TopicBlock>
                  </div>
                );
              })}
            </div>
          </div>
          <Container
            title="Waste Type by Percentage"
            style={{ flexGrow: 4, width: "44%" }}
            darkTheme={props.darkMode}
          >
            <div
              className={
                "pie-graph-container" + (props.darkMode ? " dark" : "")
              }
            >
              <div className="total-waste-counter">
                <h4>{props.totalWasteCount + "t"}</h4>
                <p>Total Waste</p>
              </div>
              <ResponsiveContainer
                width="100%"
                height={props.width * 0.148}
                style={{ position: "relative" }}
              >
                <PieChart style={{ cursor: "pointer" }}>
                  <Pie
                    data={props.recycledWasteTypes}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius="95%"
                    innerRadius="60%"
                    fill="#8884d8"
                  />
                  <Tooltip
                    cursor={false}
                    wrapperClassName={
                      "tooltip-wrapper radial-chart" +
                      (props.darkMode ? " dark" : "")
                    }
                    content={<WTPTooltip />}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <SortableTable
              id="waste-type"
              className="waste-type-table"
              width={props.width}
              header={wasteTypesDetailTableHeader}
              data={props.recycledWasteDetails}
              colorArray={props.colorArray}
              darkMode={props.darkMode}
            />
          </Container>
        </>
      )}
    </div>
  );
});

export default SummaryRow;
